import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

type DispoLevel = "date" | "time" | "widget";

interface DispoStore {
  date: string | null;
  time: string | null;
  widget: string | null;
}

export class TktDispoController implements Plugin {
  private store: DispoStore = {
    date: null,
    time: null,
    widget: null,
  };

  private dropdowns: Record<DispoLevel, HTMLElement | null> = {
    date: null,
    time: null,
    widget: null,
  };

  private lists: Record<DispoLevel, HTMLElement[]> = {
    date: [],
    time: [],
    widget: [],
  };

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.dropdowns.date = this.element.querySelector("#dropdown-date");
    this.dropdowns.time = this.element.querySelector("#dropdown-time");
    this.dropdowns.widget = this.element.querySelector("#dropdown-seat");

    this.updateListReferences();

    this.element.addEventListener("coreCalendar:change", (e) => {
      const detail = (e as CustomEvent).detail;
      if (detail.dateStr) {
        this.setDate(detail.dateStr);
      }
    });

    this.element.addEventListener(
      "tkt-availability-options-b2b:change",
      (e) => {
        const detail = (e as CustomEvent).detail;
        if (detail.value) {
          this.setTime(detail.value);
        }
      },
    );

    if (this.dropdowns.widget) {
      this.dropdowns.widget.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        if (target.closest(".tkt-dropdown__content")) {
          this.setWidget("Seat Selected");
        }
      });
    }

    (["date", "time", "widget"] as DispoLevel[]).forEach((level) => {
      const dropdown = this.dropdowns[level];
      if (!dropdown) return;
      dropdown.addEventListener("tktDropdown:expanded", () => {
        if (
          dropdown.classList.contains("has-selection") &&
          !dropdown.classList.contains("is-editing")
        ) {
          this.performEditAction(level);
        }
      });
    });

    this.element.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      const editBtn = target.closest('[data-action="edit"]');
      if (editBtn) {
        const listEl = editBtn.closest("[data-list-type]");
        if (listEl) {
          const type = listEl.getAttribute("data-list-type") as DispoLevel;
          if (type) this.handleEdit(type);
        }
      }

      const removeBtn = target.closest('[data-action="remove"]');
      if (removeBtn) {
        const listEl = removeBtn.closest("[data-list-type]");
        if (listEl) {
          const type = listEl.getAttribute("data-list-type") as DispoLevel;
          if (type) this.handleRemove(type);
        }
      }
    });

    this.element.addEventListener("coreToggle:change", (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("com-summary-card-dropdown")) {
        const detail = (e as CustomEvent).detail;
        if (detail.open) {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "";
        }
      }
    });

    const initialDate = this.lists.date[0]
      ?.querySelector('[data-content-target="date"]')
      ?.textContent?.trim();
    const initialTime = this.lists.time[0]
      ?.querySelector('[data-content-target="time"]')
      ?.textContent?.trim();
    const initialWidget = this.lists.widget[0]
      ?.querySelector('[data-content-target="widget"]')
      ?.textContent?.trim();

    if (
      initialDate &&
      initialDate !== "Seleccionar fecha" &&
      initialDate !== "-"
    ) {
      this.store.date = initialDate;
      if (this.dropdowns.date)
        this.dropdowns.date.classList.add("has-selection");
    }
    if (
      initialTime &&
      initialTime !== "Seleccionar hora" &&
      initialTime !== "-"
    ) {
      this.store.time = initialTime;
      if (this.dropdowns.time)
        this.dropdowns.time.classList.add("has-selection");
    }
    if (
      initialWidget &&
      initialWidget !== "Seleccionar zona" &&
      initialWidget !== "-"
    ) {
      this.store.widget = initialWidget;
      if (this.dropdowns.widget)
        this.dropdowns.widget.classList.add("has-selection");
    }

    this.enable("date");
    if (this.store.date) this.enable("time");
    else this.disable("time");

    if (this.store.time) this.enable("widget");
    else this.disable("widget");

    this.updateVisibilityAndContent();
  }

  private updateListReferences() {
    this.lists.date = Array.from(
      this.element.querySelectorAll('[data-list-type="date"]'),
    );
    this.lists.time = Array.from(
      this.element.querySelectorAll('[data-list-type="time"]'),
    );
    this.lists.widget = Array.from(
      this.element.querySelectorAll('[data-list-type="widget"]'),
    );
  }

  public setDate(value: string) {
    this.store.date = value;

    const dropdown = this.dropdowns.date;
    if (dropdown) {
      dropdown.classList.remove("is-editing");
      dropdown.classList.add("has-selection");
    }

    this.resetFrom("time");

    this.enable("time");

    this.closeDropdownCondition("date");
    this.openDropdown("time");

    this.updateVisibilityAndContent();
    this.emit("date:selected", { value });
  }

  public setTime(value: string) {
    this.store.time = value;

    const dropdown = this.dropdowns.time;
    if (dropdown) {
      dropdown.classList.remove("is-editing");
      dropdown.classList.add("has-selection");
    }

    this.resetFrom("widget");

    this.enable("widget");

    this.closeDropdownCondition("time");
    this.openDropdown("widget");

    this.updateVisibilityAndContent();
    this.emit("time:selected", { value });
  }

  public setWidget(value: string) {
    this.store.widget = value;

    const dropdown = this.dropdowns.widget;
    if (dropdown) {
      dropdown.classList.remove("is-editing");
      dropdown.classList.add("has-selection");
    }

    this.closeDropdownCondition("widget");

    this.updateVisibilityAndContent();
    this.emit("widget:selected", { value });
  }

  private performEditAction(level: DispoLevel) {
    this.emit(`edit:${level}`, {});

    const dropdown = this.dropdowns[level];
    if (dropdown) dropdown.classList.add("is-editing");

    if (level === "date") {
      this.resetFrom("time");
    } else if (level === "time") {
      this.resetFrom("widget");
    }
  }

  private handleEdit(level: DispoLevel) {
    this.performEditAction(level);
    this.openDropdown(level);
  }

  private handleRemove(level: DispoLevel) {
    this.emit(`remove:${level}`, {});

    const dropdown = this.dropdowns[level];
    if (dropdown) {
      dropdown.classList.remove("has-selection", "is-editing");
    }

    if (level === "date") {
      this.store.date = null;
      this.resetFrom("time");
      this.openDropdown("date");
      this.updateVisibilityAndContent();
    } else if (level === "time") {
      this.store.time = null;
      this.resetFrom("widget");
      this.openDropdown("time");
      this.updateVisibilityAndContent();
    } else if (level === "widget") {
      this.store.widget = null;
      this.openDropdown("widget");
      this.updateVisibilityAndContent();
    }
  }

  public resetFrom(level: DispoLevel) {
    if (level === "date" || level === "time") {
      this.store.time = null;
      const dd = this.dropdowns.time;
      if (dd) {
        dd.classList.remove("has-selection", "is-editing");
      }
      this.disable("time");
      this.emit("time:reset", {});
    }

    if (level === "date" || level === "time" || level === "widget") {
      this.store.widget = null;
      const dd = this.dropdowns.widget;
      if (dd) {
        dd.classList.remove("has-selection", "is-editing");
      }
      this.disable("widget");
      this.emit("widget:reset", {});
    }

    this.updateVisibilityAndContent();
  }

  private enable(level: DispoLevel) {
    const dropdown = this.dropdowns[level];
    if (dropdown) {
      dropdown.removeAttribute("disabled");
      dropdown.classList.remove("is-disabled");
      dropdown.setAttribute("aria-disabled", "false");

      const trigger = dropdown.querySelector(
        ".tkt-dropdown__header",
      ) as HTMLButtonElement | null;
      if (trigger) {
        trigger.disabled = false;
        trigger.classList.remove("is-disabled");
      }
    }
  }

  private disable(level: DispoLevel) {
    const dropdown = this.dropdowns[level];
    if (dropdown) {
      this.closeDropdown(level);

      dropdown.setAttribute("disabled", "true");
      dropdown.classList.add("is-disabled");
      dropdown.setAttribute("aria-disabled", "true");

      const trigger = dropdown.querySelector(
        ".tkt-dropdown__header",
      ) as HTMLButtonElement | null;
      if (trigger) {
        trigger.disabled = true;
        trigger.classList.add("is-disabled");
      }
    }
  }

  private closeDropdownCondition(level: DispoLevel) {
    const isMobile = window.innerWidth <= 940;
    if (!isMobile) {
      this.closeDropdown(level);
    }
  }

  private closeDropdown(level: DispoLevel) {
    const dropdown = this.dropdowns[level];
    if (dropdown) {
      const trigger = dropdown.querySelector(".tkt-dropdown__header");
      if (trigger && dropdown.getAttribute("aria-expanded") === "true") {
        (trigger as HTMLElement).click();
      }
    }
  }

  private openDropdown(level: DispoLevel) {
    const dropdown = this.dropdowns[level];
    if (dropdown) {
      const trigger = dropdown.querySelector(".tkt-dropdown__header");
      if (trigger && dropdown.getAttribute("aria-expanded") !== "true") {
        (trigger as HTMLElement).click();
      }
    }
  }

  private updateVisibilityAndContent() {
    this.lists.date.forEach((el) => {
      if (this.store.date) {
        const target = el.querySelector('[data-content-target="date"]');
        if (target) target.textContent = this.store.date;
        if (!el.closest(".tkt-mobile-summary")) el.style.display = "";
      } else {
        if (!el.closest(".tkt-mobile-summary")) el.style.display = "none";
      }
    });

    this.lists.time.forEach((el) => {
      if (this.store.time) {
        const target = el.querySelector('[data-content-target="time"]');
        if (target) target.textContent = this.store.time;
        if (!el.closest(".tkt-mobile-summary")) el.style.display = "";
      } else {
        if (!el.closest(".tkt-mobile-summary")) el.style.display = "none";
      }
    });

    this.lists.widget.forEach((el) => {
      if (this.store.widget) {
        const target = el.querySelector('[data-content-target="widget"]');
        if (target) target.textContent = this.store.widget;
        if (!el.closest(".tkt-mobile-summary")) el.style.display = "";
      } else {
        if (!el.closest(".tkt-mobile-summary")) el.style.display = "none";
      }
    });
  }

  private emit(eventName: string, detail: any) {
    this.element.dispatchEvent(
      new CustomEvent(eventName, { detail, bubbles: true }),
    );
  }
}

PluginRegistry.register("tktDispoController", TktDispoController);
